///
/// EarthBrowser - Data Browser Controls
///
/// Toolbar with search, filter, and pagination controls
/// Created by Matt Giger on December 17, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { cn } from "@/utils/utils"
import { formatDistanceToNow } from "date-fns"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Filter, RefreshCw, Search, X } from "lucide-react"
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react"

export interface DataBrowserControlsProps {
	// Search
	searchQuery?: string
	onSearchChange?: (query: string) => void
	searchPlaceholder?: string

	// Pagination
	currentPage?: number
	totalPages?: number
	pageSize?: number
	totalCount?: number
	onPageChange?: (page: number) => void
	onPageSizeChange?: (pageSize: number) => void

	// Sync status
	totalDatabaseCount?: number
	lastSyncTime?: string
	onRefresh?: () => void

	// Filtering
	hasFilters?: boolean
	onFilterClick?: () => void

	// Actions
	actions?: React.ReactNode

	// Display info
	showResultsInfo?: boolean
}

export interface DataBrowserControlsHandle {
	focusSearch: () => void
}

export const DataBrowserControls = forwardRef<DataBrowserControlsHandle, DataBrowserControlsProps>(function DataBrowserControls({
	searchQuery = "",
	onSearchChange,
	searchPlaceholder = "Search...",
	currentPage = 1,
	totalPages = 1,
	pageSize = 50,
	totalCount = 0,
	totalDatabaseCount,
	lastSyncTime,
	onPageChange,
	onRefresh,
	hasFilters = false,
	onFilterClick,
	actions,
	showResultsInfo = true,
}, ref) {
	const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery)
	const searchInputRef = useRef<HTMLInputElement>(null)

	// Expose focus method to parent
	useImperativeHandle(ref, () => ({
		focusSearch: () => {
			searchInputRef.current?.focus()
		}
	}), [])

	// Handle search input
	const handleSearchChange = useCallback((value: string) => {
		setLocalSearchQuery(value)
		onSearchChange?.(value)
	}, [onSearchChange])

	// Clear search
	const handleClearSearch = useCallback(() => {
		setLocalSearchQuery("")
		onSearchChange?.("")
	}, [onSearchChange])

	// Pagination handlers
	const canGoPrevious = currentPage > 1
	const canGoNext = currentPage < totalPages

	const handleFirstPage = useCallback(() => {
		if (canGoPrevious) onPageChange?.(1)
	}, [canGoPrevious, onPageChange])

	const handlePreviousPage = useCallback(() => {
		if (canGoPrevious) onPageChange?.(currentPage - 1)
	}, [canGoPrevious, currentPage, onPageChange])

	const handleNextPage = useCallback(() => {
		if (canGoNext) onPageChange?.(currentPage + 1)
	}, [canGoNext, currentPage, onPageChange])

	const handleLastPage = useCallback(() => {
		if (canGoNext) onPageChange?.(totalPages)
	}, [canGoNext, totalPages, onPageChange])

	// Calculate displayed range
	const startRow = (currentPage - 1) * pageSize + 1
	const endRow = Math.min(currentPage * pageSize, totalCount)

	// Format sync time for display
	const syncTimeText = lastSyncTime
		? formatDistanceToNow(new Date(lastSyncTime), { addSuffix: true })
		: undefined

	return (
		<div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2 border-b border-white/10 bg-black/20">
			{/* Search — takes full width on narrow screens to avoid overlap */}
			{onSearchChange && (
				<div className="flex-1 basis-48 min-w-0 flex items-center gap-2 bg-white/5 rounded-lg px-2.5 py-1.5">
					<Search className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
					<input
						ref={searchInputRef}
						type="text"
						value={localSearchQuery}
						onChange={(e) => handleSearchChange(e.target.value)}
						placeholder={searchPlaceholder}
						className="flex-1 bg-transparent text-white placeholder:text-white/40 focus:outline-none text-sm"
					/>
					{localSearchQuery && (
						<button
							onClick={handleClearSearch}
							className="p-0.5 rounded hover:bg-white/10 transition-colors flex-shrink-0"
							aria-label="Clear search"
						>
							<X className="w-3.5 h-3.5 text-white/60 hover:text-white/80" />
						</button>
					)}
				</div>
			)}

			{/* Filter button */}
			{onFilterClick && (
				<button
					onClick={onFilterClick}
					className={cn(
						"flex-shrink-0 px-2.5 py-1.5 rounded-lg border transition-colors flex items-center gap-1.5",
						hasFilters
							? "bg-cyan-500/20 border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/30"
							: "bg-white/5 border-white/10 text-white/60 hover:bg-white/10"
					)}
					aria-label="Open filter"
				>
					<Filter className="w-3.5 h-3.5" />
					{hasFilters && <span className="text-xs font-semibold">Active</span>}
				</button>
			)}

			{/* Custom actions */}
			{actions && <div className="flex-shrink-0">{actions}</div>}

			{/* Refresh button with sync time */}
			{onRefresh && (
				<button
					onClick={onRefresh}
					className="p-1.5 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
					aria-label="Refresh data"
					title={syncTimeText ? `Last synced ${syncTimeText}` : 'Refresh data'}
				>
					<RefreshCw className="w-3.5 h-3.5 text-white/60 hover:text-white/80" />
				</button>
			)}

			{/* Results info */}
			{showResultsInfo && (
				<div className="text-xs text-white/60 whitespace-nowrap flex-shrink-0">
					{totalCount > 0 ? (
						<span>
							{startRow.toLocaleString()}-{endRow.toLocaleString()} of {totalCount.toLocaleString()}
							{totalDatabaseCount && totalDatabaseCount !== totalCount && (
								<span className="text-white/40"> ({totalDatabaseCount.toLocaleString()} total)</span>
							)}
						</span>
					) : (
						<span>No results</span>
					)}
				</div>
			)}

			{/* Pagination */}
			{onPageChange && totalPages > 1 && (
				<div className="flex items-center gap-1 flex-shrink-0">
					<button
						onClick={handleFirstPage}
						disabled={!canGoPrevious}
						className={cn(
							"rounded transition-colors",
							canGoPrevious
								? "hover:bg-white/10 text-white/80"
								: "opacity-30 cursor-not-allowed text-white/40"
						)}
						aria-label="First page"
					>
						<ChevronsLeft className="w-3.5 h-3.5" />
					</button>

					<button
						onClick={handlePreviousPage}
						disabled={!canGoPrevious}
						className={cn(
							"rounded transition-colors",
							canGoPrevious
								? "hover:bg-white/10 text-white/80"
								: "opacity-30 cursor-not-allowed text-white/40"
						)}
						aria-label="Previous page"
					>
						<ChevronLeft className="w-3.5 h-3.5" />
					</button>

					<div className="px-1 py-0.5 rounded bg-white/5 border border-white/10">
						<span className="text-xs text-white/80">
							{currentPage}/{totalPages}
						</span>
					</div>

					<button
						onClick={handleNextPage}
						disabled={!canGoNext}
						className={cn(
							"rounded transition-colors",
							canGoNext
								? "hover:bg-white/10 text-white/80"
								: "opacity-30 cursor-not-allowed text-white/40"
						)}
						aria-label="Next page"
					>
						<ChevronRight className="w-3.5 h-3.5" />
					</button>

					<button
						onClick={handleLastPage}
						disabled={!canGoNext}
						className={cn(
							"rounded transition-colors",
							canGoNext
								? "hover:bg-white/10 text-white/80"
								: "opacity-30 cursor-not-allowed text-white/40"
						)}
						aria-label="Last page"
					>
						<ChevronsRight className="w-3.5 h-3.5" />
					</button>
				</div>
			)}
		</div>
	)
})

